import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { AppShell } from '../components/AppShell';
import { EmptyState } from '../components/EmptyState';
import { Icon } from '../components/Icon';
import { SectionHeader, Skeleton } from '../components/ui';
import { useTestimonials } from '../features/testimonials/useTestimonials';
import styles from './TestimonialsPage.module.scss';

function StarRow({ rating }: { readonly rating: number }) {
  return (
    <div className={styles.stars} aria-label={`${rating}/5`}>
      {Array.from({ length: 5 }, (_, i) => (
        <span key={i} className={i < rating ? styles.starFilled : styles.starEmpty}>
          <Icon name="star" size={16} />
        </span>
      ))}
    </div>
  );
}

function formatDate(iso: string, locale: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat(locale === 'uk' ? 'uk-UA' : 'en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

export function TestimonialsPage() {
  const { t, i18n } = useTranslation();
  const { data, isLoading, isError, error } = useTestimonials({ limit: 100 });

  if (isLoading) {
    return (
      <AppShell>
        <div className={styles.page}>
          <SectionHeader title={t('testimonials.title')} subtitle={t('testimonials.subtitle')} />
          <div className={styles.list}>
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} height={96} />
            ))}
          </div>
        </div>
      </AppShell>
    );
  }

  if (isError) {
    return (
      <AppShell>
        <div className={styles.page}>
          <SectionHeader title={t('testimonials.title')} />
          <EmptyState
            title={t('testimonials.loadErrorTitle')}
            description={error instanceof Error ? error.message : t('testimonials.loadErrorDescription')}
          />
        </div>
      </AppShell>
    );
  }

  const items = data?.items ?? [];

  return (
    <AppShell>
      <div className={styles.page}>
        <SectionHeader
          title={t('testimonials.title')}
          subtitle={t('testimonials.subtitle', { count: data?.total ?? items.length })}
        />
        {items.length === 0 ? (
          <EmptyState
            icon={<Icon name="star" size={48} />}
            title={t('testimonials.emptyTitle')}
            description={t('testimonials.emptyDescription')}
          />
        ) : (
          <div className={styles.list}>
            {items.map((item) => (
              <article key={item.id} className={styles.card}>
                <div className={styles.cardHead}>
                  <Link
                    to={`/clients/${item.patient_id}`}
                    className={styles.patient}
                    title={t('testimonials.viewProfile')}
                  >
                    <Icon name="person" size={16} />
                    <span className={styles.patientName}>{item.patient_name}</span>
                    <span className={styles.patientId}>{item.patient_display_id}</span>
                  </Link>
                  <StarRow rating={item.rating} />
                </div>
                <div className={styles.meta}>
                  <span>
                    {t('testimonials.stageN', { number: item.cycle_number })}
                    {item.stage_title ? ` · ${item.stage_title}` : ''}
                  </span>
                  <span>{formatDate(item.created_at, i18n.language)}</span>
                </div>
                {item.comment ? (
                  <p className={styles.comment}>{item.comment}</p>
                ) : (
                  <p className={styles.noComment}>{t('testimonials.noComment')}</p>
                )}
              </article>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
